import { type TimelineFeatures, useFeatures } from "@expert/features";
import { Flex } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";

import type { TimelineListProps } from "./types";

import { useTimelineStore } from "../../state";
import { isProactiveGuidanceType } from "../../utils";
import { ProactiveGuidanceMessageBubble } from "../proactive-guidance/ProactiveGuidanceMessageBubble";
import { TextHighlightArea } from "../text-highlight";
import { List, TimelineListWrapper } from "./elements";
import { generateTimelineItems } from "./generateTimelineItems";
import { TimelineActionGroup } from "./TimelineActionGroup";
import classes from "./TimelineList.module.css";
import { useScrollHandler } from "./useScrollHandler";

export function TimelineList({
    gaiaPayloadProps,
    logger,
    scrollRef,
    IssueSummaryComponent,
    guideSlotRef,
}: TimelineListProps) {
    const {
        features: { isProactiveGuidanceEnabled, isTextHighlightAssistEnabled },
    } = useFeatures<TimelineFeatures>();
    const { markAllMessagesAsRead, getTimelineState, currentSessionId } = useTimelineStore();
    const { messages, autoCompleteSuggestions } = getTimelineState(currentSessionId);
    const { mergedRef, isScrollAtBottom } = useScrollHandler({ scrollRef, messages: messages.length });
    const [shouldShowNewMessageButton, setShouldShowNewMessageButton] = useState(false);

    const timelineItems = useMemo(
        () =>
            generateTimelineItems({
                logger,
                messages,
                scrollRef,
                gaiaPayloadProps,
            }),
        [messages, scrollRef, logger, gaiaPayloadProps],
    );

    const unreadMessages = useMemo(
        () => messages.filter((message) => message.isUnread && !isProactiveGuidanceType(message.type)).length,
        [messages],
    );

    useEffect(() => {
        setShouldShowNewMessageButton(unreadMessages > 0 && !isScrollAtBottom);
        if (isScrollAtBottom && unreadMessages > 0) {
            markAllMessagesAsRead(currentSessionId);
        }
    }, [unreadMessages, isScrollAtBottom, markAllMessagesAsRead, currentSessionId]);

    return (
        <Flex className={classes.column} direction="column">
            {guideSlotRef && <div className={classes.guideSlot} data-testid="timeline-guide-slot" ref={guideSlotRef} />}
            <TextHighlightArea
                isEnabled={isTextHighlightAssistEnabled}
                surface="timeline"
                logger={logger}
                gaiaPayloadProps={gaiaPayloadProps}
            >
                {isProactiveGuidanceEnabled && (
                    <ProactiveGuidanceMessageBubble
                        gaiaPayloadProps={gaiaPayloadProps}
                        logger={logger}
                        messages={messages}
                        currentSessionId={currentSessionId}
                        scrollRef={scrollRef}
                    />
                )}
                <TimelineListWrapper mergedRef={mergedRef}>
                    <List data-testid="timeline-list" IssueSummaryComponent={IssueSummaryComponent}>
                        {timelineItems}
                    </List>
                </TimelineListWrapper>
            </TextHighlightArea>
            <TimelineActionGroup
                gaiaPayloadProps={gaiaPayloadProps}
                logger={logger}
                scrollRef={scrollRef}
                shouldShowNewMessageButton={shouldShowNewMessageButton}
                unreadMessages={unreadMessages}
                proactiveSuggestions={autoCompleteSuggestions}
            />
        </Flex>
    );
}
